import React, { useCallback, useState } from 'react';
import { format, parse, isValid, addYears, isAfter } from 'date-fns';
import classNames from 'classnames';
import { Grid, TextField, IconButton, Tooltip, Popover } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import {
  ChevronRight,
  InfoOutlined,
  Today as CalendarIcon
} from '@mui/icons-material';
// @ts-expect-error: coming from react-date-range package
import { DateRangePicker, type Range } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { colors } from '../../../Utilities/LenoxColors';

interface IDateFieldProps {
  value: string; // Expected format: "MM/DD/YYYY" or empty
  name: string;
  label: string;
  onChange: (name: string, value: string) => void;
  classes: Record<string, string>;
  blur?: boolean;
  tooltipMessage?: string;
  message?: string;
  error?: boolean;
  disabled?: boolean;
  includeSubmit?: boolean;
  fieldGrid?: number;
  buttonsGrid?: number;
  submitRender?: () => React.ReactNode;
  fullWidth?: boolean;
  inputProps?: Record<string, string>;
}

const styles = ({ spacing }: { spacing: (unit: number) => string }) => ({
  margin: {
    margin: spacing(1)
  },
  textField: {
    width: '100%',
    color: `${colors.lenoxLight1} !important`
  },
  submit: {
    display: 'inline-flex'
  },
  tooltip: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  dateRangePicker: {
    '& .rdrDefinedRangesWrapper': {
      display: 'none !important'
    }
  }
});

const DateField: React.FC<IDateFieldProps> = ({
  value,
  name,
  label,
  onChange,
  classes,
  blur = false,
  tooltipMessage = '',
  message = '',
  error = false,
  disabled = false,
  includeSubmit = false,
  fieldGrid,
  buttonsGrid,
  submitRender,
  fullWidth = false,
  inputProps = {}
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Set ranges to preselect the value date if valid and not after maxDate; otherwise, use today's date
  const getParsedDate = useCallback((): Range => {
    const today = new Date();
    const maxDate = addYears(today, 20);
    if (value) {
      const parsedDate = parse(value, 'MM/dd/yyyy', new Date());
      // Use the value date if valid and not after maxDate
      if (isValid(parsedDate) && !isAfter(parsedDate, maxDate)) {
        return {
          startDate: parsedDate,
          endDate: parsedDate,
          key: 'selection'
        };
      }
    }
    // Default to today if value is empty, invalid, or after maxDate
    return {
      startDate: today,
      endDate: today,
      key: 'selection'
    };
  }, [value]);

  const formatInput = useCallback((input: string): string => {
    const digits = input.replace(/\D/g, '');
    let formatted = '';

    if (digits.length > 0) {
      formatted = digits.substring(0, 2);
    }
    if (digits.length > 2) {
      formatted = `${formatted}/${digits.substring(2, 4)}`;
    }
    if (digits.length > 4) {
      formatted = `${formatted}/${digits.substring(4, 8)}`;
    }

    return formatted;
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const formattedValue = formatInput(event.target.value);
      if (!blur) {
        onChange(name, formattedValue);
      }
    },
    [blur, formatInput, name, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (blur) {
        const formattedValue = formatInput(event.target.value);
        onChange(name, formattedValue);
      }
    },
    [blur, formatInput, name, onChange]
  );

  const handleCalendarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (ranges: { [key: string]: Range }) => {
    const { startDate } = ranges.selection;
    if (startDate && isValid(startDate)) {
      const formattedDate = format(startDate, 'MM/dd/yyyy');
      onChange(name, formattedDate);
    }
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-picker-popover' : undefined;

  const renderField = () => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <TextField
        label={
          tooltipMessage ? (
            <div className={classes.tooltip}>
              {label}
              <Tooltip title={tooltipMessage} placement="bottom">
                <InfoOutlined style={{ height: '.7em', width: '.7em' }} />
              </Tooltip>
            </div>
          ) : (
            label
          )
        }
        fullWidth={fullWidth}
        inputProps={{
          maxLength: 10, // For "MM/DD/YYYY"
          ...inputProps
        }}
        error={error}
        id={label}
        className={classNames(classes.textField)}
        variant="outlined"
        InputLabelProps={{ shrink: true, style: { pointerEvents: 'auto' } }}
        helperText={tooltipMessage ? '' : message}
        placeholder="MM/DD/YYYY"
        type="text"
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
      />
      <IconButton
        onClick={handleCalendarClick}
        className={classes.calendarIcon}
        disabled={disabled}
        size="small"
      >
        <CalendarIcon />
      </IconButton>
    </div>
  );

  const renderSubmitButton = () => (
    <IconButton className={classes.submit} size="large">
      <ChevronRight />
    </IconButton>
  );

  return (
    <Grid key={name} container justifyContent="center" alignItems="center">
      <Grid item xs={12} className={classNames(classes.margin)}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={includeSubmit ? fieldGrid || 10 : 12}>
            {renderField()}
          </Grid>
          {includeSubmit && (
            <Grid item xs={buttonsGrid || 2}>
              {submitRender ? submitRender() : renderSubmitButton()}
            </Grid>
          )}
        </Grid>
      </Grid>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <div className={classes.dateRangePicker}>
          <DateRangePicker
            onChange={handleDateSelect}
            ranges={[getParsedDate()]}
            showDateDisplay={false}
            showSelectionPreview={false}
            moveRangeOnFirstSelection
            months={1}
            direction="horizontal"
            retainEndDateOnFirstSelection={false}
            maxDate={addYears(new Date(), 20)} // 20 years from today
          />
        </div>
      </Popover>
    </Grid>
  );
};

export default withStyles(DateField, styles);
